import { type Frequency } from "@/lib/billingSchedule";

/**
 * Billing cycle mapping configuration
 */
interface BillingCycleMapping {
  frequency: Frequency;
  customDays?: number;
}

/**
 * Utility class for mapping between database billing_cycle values and internal frequency types
 */
export class BillingCycleMapper {
  private static readonly BILLING_CYCLE_MAP: Record<
    string,
    BillingCycleMapping
  > = {
    weekly: { frequency: "weekly" },
    fortnightly: { frequency: "fortnightly" },
    monthly: { frequency: "monthly" },
    every_2_months: { frequency: "every_2_months" },
    quarterly: { frequency: "quarterly" },
    yearly: { frequency: "yearly" },
  };

  private static readonly DEFAULT_CUSTOM_DAYS = 30;

  /**
   * Convert database billing_cycle to frequency and customDays
   * @param billingCycle - The billing cycle from database
   * @returns {BillingCycleMapping} Frequency and optional customDays
   */
  static mapBillingCycleToFrequency(billingCycle: string): BillingCycleMapping {
    // Handle custom billing cycles (e.g., "custom_45")
    if (billingCycle.startsWith("custom_")) {
      const daysStr = billingCycle.split("_")[1];
      const customDays = this.parseCustomDays(daysStr);

      return {
        frequency: "custom",
        customDays,
      };
    }

    // Handle standard billing cycles
    const mapping = this.BILLING_CYCLE_MAP[billingCycle];
    if (mapping) {
      return mapping;
    }

    // Default fallback to monthly
    console.warn(
      `Unknown billing cycle: ${billingCycle}, defaulting to monthly`,
    );
    return { frequency: "monthly" };
  }

  /**
   * Parse custom days from string with validation
   * @param daysStr - String representation of days
   * @returns {number} Validated number of days
   */
  private static parseCustomDays(daysStr: string | undefined): number {
    if (!daysStr) {
      return this.DEFAULT_CUSTOM_DAYS;
    }

    const parsed = parseInt(daysStr, 10);

    // Validate parsed number
    if (isNaN(parsed) || parsed <= 0) {
      console.warn(
        `Invalid custom days: ${daysStr}, using default ${this.DEFAULT_CUSTOM_DAYS}`,
      );
      return this.DEFAULT_CUSTOM_DAYS;
    }

    // Reasonable bounds check (1 day to 5 years)
    if (parsed > 1825) {
      // 5 years
      console.warn(`Custom days too large: ${parsed}, capping at 365 days`);
      return 365;
    }

    return parsed;
  }

  /**
   * Check if billing cycle represents a valid scheduled billing
   * @param billingCycle - The billing cycle to validate
   * @returns {boolean} True if valid for scheduling
   */
  static isValidScheduledBilling(billingCycle: string): boolean {
    return billingCycle !== "pay_as_you_go" && billingCycle.length > 0;
  }

  /**
   * Get all supported standard billing cycles
   * @returns {string[]} Array of supported billing cycle keys
   */
  static getSupportedBillingCycles(): string[] {
    return Object.keys(this.BILLING_CYCLE_MAP);
  }

  /**
   * Validate billing cycle format
   * @param billingCycle - The billing cycle to validate
   * @returns {boolean} True if format is valid
   */
  static isValidBillingCycleFormat(billingCycle: string): boolean {
    // Check if it's a standard cycle
    if (this.BILLING_CYCLE_MAP[billingCycle]) {
      return true;
    }

    // Check if it's a valid custom cycle
    if (billingCycle.startsWith("custom_")) {
      const daysStr = billingCycle.split("_")[1];
      const days = parseInt(daysStr, 10);
      return !isNaN(days) && days > 0 && days <= 1825;
    }

    // Check for pay-as-you-go
    if (billingCycle === "pay_as_you_go") {
      return true;
    }

    return false;
  }
}
